import { Contact_StatusSchema } from "@/src/gql/validation";

export interface FieldValidation {
  isValid: boolean;
  errors: string[];
  sanitizedValue?: unknown;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[\d\s\-+().]+$/;

export function validateEmail(value: unknown): FieldValidation {
  if (value === null || value === undefined) {
    return { isValid: true, errors: [], sanitizedValue: null };
  }
  if (typeof value !== "string") {
    return { isValid: false, errors: ["Email must be a string"] };
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return { isValid: true, errors: [], sanitizedValue: null };
  }
  if (!EMAIL_REGEX.test(trimmed)) {
    return { isValid: false, errors: ["Email format is invalid"] };
  }
  return { isValid: true, errors: [], sanitizedValue: trimmed.toLowerCase() };
}

export function validatePhone(value: unknown): FieldValidation {
  if (value === null || value === undefined) {
    return { isValid: true, errors: [], sanitizedValue: null };
  }
  if (typeof value !== "string") {
    return { isValid: false, errors: ["Phone must be a string"] };
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return { isValid: true, errors: [], sanitizedValue: null };
  }
  if (!PHONE_REGEX.test(trimmed)) {
    return { isValid: false, errors: ["Phone format is invalid"] };
  }
  return { isValid: true, errors: [], sanitizedValue: trimmed };
}

export function validateStatus(value: unknown): FieldValidation {
  if (value === null || value === undefined) {
    return { isValid: true, errors: [], sanitizedValue: null };
  }
  try {
    const validated = Contact_StatusSchema.parse(value);
    return { isValid: true, errors: [], sanitizedValue: validated };
  } catch {
    return {
      isValid: false,
      errors: [
        "Status must be one of: churned, customer, inactive, lead, other_non_revenue, personal, prospect, system_notification",
      ],
    };
  }
}

export function validateTags(value: unknown): FieldValidation {
  if (value === null || value === undefined) {
    return { isValid: true, errors: [], sanitizedValue: null };
  }
  if (!Array.isArray(value)) {
    return { isValid: false, errors: ["Tags must be an array"] };
  }
  const errors: string[] = [];
  const sanitized: string[] = [];
  for (const tag of value) {
    if (tag === null || tag === undefined) continue;
    if (typeof tag !== "string") {
      errors.push(`Tag must be a string, got ${typeof tag}`);
      continue;
    }
    const trimmed = tag.trim().toLowerCase();
    if (trimmed.length === 0) continue;
    if (!sanitized.includes(trimmed)) sanitized.push(trimmed);
  }
  if (errors.length > 0) return { isValid: false, errors };
  return { isValid: true, errors: [], sanitizedValue: sanitized };
}

export function validateCustomFields(value: unknown): FieldValidation {
  if (value === null || value === undefined) {
    return { isValid: true, errors: [], sanitizedValue: null };
  }
  if (typeof value !== "object" || Array.isArray(value)) {
    return { isValid: false, errors: ["custom_fields must be an object"] };
  }
  try {
    JSON.stringify(value);
    return { isValid: true, errors: [], sanitizedValue: value };
  } catch {
    return { isValid: false, errors: ["custom_fields must contain JSON-serializable values"] };
  }
}

export function validatePhoneAliases(value: unknown): FieldValidation {
  if (value === null || value === undefined) {
    return { isValid: true, errors: [], sanitizedValue: null };
  }
  if (!Array.isArray(value)) {
    return { isValid: false, errors: ["phone_aliases must be an array"] };
  }
  const errors: string[] = [];
  const sanitized: string[] = [];
  for (const alias of value) {
    if (alias === null || alias === undefined) continue;
    if (typeof alias !== "string") {
      errors.push(`Phone alias must be a string, got ${typeof alias}`);
      continue;
    }
    const trimmed = alias.trim();
    if (trimmed.length < 7) {
      errors.push(`Phone number is too short: ${trimmed}`);
      continue;
    }
    const digits = trimmed.replace(/\D/g, "");
    if (digits.length < 10) {
      errors.push(`Invalid phone format: ${trimmed}`);
      continue;
    }
    if (!sanitized.some((s) => s.replace(/\D/g, "").slice(-10) === digits.slice(-10))) {
      sanitized.push(trimmed);
    }
  }
  if (errors.length > 0) return { isValid: false, errors };
  return { isValid: true, errors: [], sanitizedValue: sanitized };
}

export function validateEmailAliases(value: unknown): FieldValidation {
  if (value === null || value === undefined) {
    return { isValid: true, errors: [], sanitizedValue: null };
  }
  if (!Array.isArray(value)) {
    return { isValid: false, errors: ["email_aliases must be an array"] };
  }
  const errors: string[] = [];
  const sanitized: string[] = [];
  for (const alias of value) {
    if (alias === null || alias === undefined) continue;
    if (typeof alias !== "string") {
      errors.push(`Email alias must be a string, got ${typeof alias}`);
      continue;
    }
    const trimmed = alias.trim().toLowerCase();
    if (!EMAIL_REGEX.test(trimmed)) {
      errors.push(`Invalid email format: ${trimmed}`);
      continue;
    }
    if (!sanitized.includes(trimmed)) sanitized.push(trimmed);
  }
  if (errors.length > 0) return { isValid: false, errors };
  return { isValid: true, errors: [], sanitizedValue: sanitized };
}

export function validateSocialLinks(value: unknown): FieldValidation {
  if (value === null || value === undefined) {
    return { isValid: true, errors: [], sanitizedValue: null };
  }
  if (typeof value !== "object" || Array.isArray(value)) {
    return { isValid: false, errors: ["social_links must be an object"] };
  }
  try {
    JSON.stringify(value);
    return { isValid: true, errors: [], sanitizedValue: value };
  } catch {
    return { isValid: false, errors: ["social_links must contain JSON-serializable values"] };
  }
}

export function validateBoolean(value: unknown, fieldName: string): FieldValidation {
  if (value === null || value === undefined) {
    return { isValid: true, errors: [], sanitizedValue: null };
  }
  if (typeof value !== "boolean") {
    return { isValid: false, errors: [`${fieldName} must be a boolean`] };
  }
  return { isValid: true, errors: [], sanitizedValue: value };
}

export function validateString(value: unknown, fieldName: string): FieldValidation {
  if (value === null || value === undefined) {
    return { isValid: true, errors: [], sanitizedValue: null };
  }
  if (typeof value !== "string") {
    return { isValid: false, errors: [`${fieldName} must be a string`] };
  }
  return { isValid: true, errors: [], sanitizedValue: value.trim() };
}

export function validateStringArray(value: unknown, fieldName: string): FieldValidation {
  if (value === null || value === undefined) {
    return { isValid: true, errors: [], sanitizedValue: null };
  }
  if (!Array.isArray(value)) {
    return { isValid: false, errors: [`${fieldName} must be an array`] };
  }
  const errors: string[] = [];
  const sanitized: string[] = [];
  for (const item of value) {
    if (item === null || item === undefined) continue;
    if (typeof item !== "string") {
      errors.push(`${fieldName} items must be strings, got ${typeof item}`);
      continue;
    }
    const trimmed = item.trim();
    if (trimmed.length > 0 && !sanitized.includes(trimmed)) sanitized.push(trimmed);
  }
  if (errors.length > 0) return { isValid: false, errors };
  return { isValid: true, errors: [], sanitizedValue: sanitized };
}

export function validateDateString(value: unknown, fieldName: string): FieldValidation {
  if (value === null || value === undefined) {
    return { isValid: true, errors: [], sanitizedValue: null };
  }
  if (typeof value !== "string") {
    return { isValid: false, errors: [`${fieldName} must be a string (ISO 8601 date)`] };
  }
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return { isValid: false, errors: [`${fieldName} must be a valid ISO 8601 date`] };
    }
    return { isValid: true, errors: [], sanitizedValue: date.toISOString() };
  } catch {
    return { isValid: false, errors: [`${fieldName} must be a valid ISO 8601 date`] };
  }
}

export function validateUUID(value: unknown, fieldName: string): FieldValidation {
  if (value === null || value === undefined) {
    return { isValid: true, errors: [], sanitizedValue: null };
  }
  if (typeof value !== "string") {
    return { isValid: false, errors: [`${fieldName} must be a string (UUID)`] };
  }
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(value)) {
    return { isValid: false, errors: [`${fieldName} must be a valid UUID`] };
  }
  return { isValid: true, errors: [], sanitizedValue: value };
}
